// src/pages/harvesting/formula/Calculation.js

/**
 * Fungsi untuk menghitung Pencapaian Panen
 * @param {number|string} targetPanen - Target panen
 * @param {number|string} realPanen - Real panen
 * @returns {string} - Persentase pencapaian panen
 */
export const calculatePencapaianPanen = (targetPanen, realPanen) => {
    try {
        // Konversi ke number, hilangkan koma jika ada
        const target = parseFloat(String(targetPanen).replace(',', '.'));
        const real = parseFloat(String(realPanen).replace(',', '.'));
        
        // Validasi input
        if (isNaN(target) || isNaN(real) || target === 0) {
            return '-';
        }
        
        // Hitung persentase
        const persentase = (real / target) * 100;
        
        // Gunakan Math.round() untuk membulatkan ke bilangan bulat terdekat
        const roundedPersentase = Math.round(persentase);
        
        // Format dan tambahkan %
        return `${roundedPersentase}%`;
    } catch (error) {
        console.error('Error calculating Pencapaian Panen:', error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung Luas Terpanen
 * @param {string} tanggalClosing - Tanggal closing
 * @param {number|string} pencapaianPanen - Pencapaian panen (dalam persentase string, misal "85.72%")
 * @param {number|string} luas - Luas lahan
 * @returns {string} - Luas terpanen atau tanda "-"
 */
export const calculateLuasTerpanen = (tanggalClosing, pencapaianPanen, luas) => {
    try {
        // Cek jika tanggal closing kosong atau '-'
        if (!tanggalClosing || tanggalClosing === '' || tanggalClosing === '-') {
            // Ekstrak angka dari persentase pencapaian panen
            let persentaseValue = 0;
            if (typeof pencapaianPanen === 'string' && pencapaianPanen.includes('%')) {
                persentaseValue = parseFloat(pencapaianPanen.replace('%', '').replace(',', '.')) / 100;
            } else {
                // Jika pencapaianPanen sudah dalam bentuk angka (0-1)
                persentaseValue = parseFloat(String(pencapaianPanen).replace(',', '.'));
                if (persentaseValue > 1) {
                    // Jika lebih dari 1, asumsikan dalam persentase
                    persentaseValue = persentaseValue / 100;
                }
            }
            
            // Konversi luas ke number
            const luasValue = parseFloat(String(luas).replace(',', '.'));
            
            if (isNaN(persentaseValue) || isNaN(luasValue)) {
                return '-';
            }
            
            // Hitung luas terpanen
            const luasTerpanen = persentaseValue * luasValue;
            
            // Format dengan 2 angka desimal
            return luasTerpanen.toFixed(2).replace('.', ',');
        } else {
            // Jika tanggal closing tidak kosong, tampilkan "-"
            return '-';
        }
    } catch (error) {
        console.error('Error calculating Luas Terpanen:', error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung Yield
 * @param {number|string} realPanen - Real panen
 * @param {number|string} luas - Luas lahan
 * @returns {string} - Yield yang diformat
 */
export const calculateYield = (realPanen, luas) => {
    try {
        // Konversi ke number
        const real = parseFloat(String(realPanen).replace(',', '.'));
        const luasValue = parseFloat(String(luas).replace(',', '.'));
        
        // Validasi input
        if (isNaN(real) || isNaN(luasValue) || luasValue === 0) {
            return '-';
        }
        
        // Hitung yield
        const yieldValue = real / luasValue;
        
        // Format dengan 2 angka desimal, contoh: 24,99
        return yieldValue.toFixed(2).replace('.', ',');
    } catch (error) {
        console.error('Error calculating Yield:', error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung Rencana Yield
 * @param {number|string} targetPanen - Target panen
 * @param {number|string} luas - Luas lahan
 * @returns {string} - Rencana yield yang diformat
 */
export const calculateRencanaYield = (targetPanen, luas) => {
    try {
        // Konversi ke number
        const target = parseFloat(String(targetPanen).replace(',', '.'));
        const luasValue = parseFloat(String(luas).replace(',', '.'));
        
        // Validasi input
        if (isNaN(target) || isNaN(luasValue) || luasValue === 0) {
            return '-';
        }
        
        // Hitung rencana yield
        const rencanaYield = target / luasValue;
        
        // Format dengan 2 angka desimal, contoh: 85,72
        return rencanaYield.toFixed(2).replace('.', ',');
    } catch (error) {
        console.error('Error calculating Rencana Yield:', error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung Var Yield Renc. VS Real
 * @param {number|string} yieldValue - Yield (dalam format string dengan koma)
 * @param {number|string} targetPanen - Target panen
 * @param {number|string} luas - Luas lahan
 * @returns {string} - Variasi yield yang diformat
 */
export const calculateVarYieldRencVsReal = (yieldValue, targetPanen, luas) => {
    try {
        // Konversi yield dari string format "24,99" ke number
        const yieldNum = typeof yieldValue === 'string' 
            ? parseFloat(yieldValue.replace(',', '.'))
            : parseFloat(String(yieldValue).replace(',', '.'));
        
        // Konversi lainnya ke number
        const target = parseFloat(String(targetPanen).replace(',', '.'));
        const luasValue = parseFloat(String(luas).replace(',', '.'));
        
        // Validasi input
        if (isNaN(yieldNum) || isNaN(target) || isNaN(luasValue) || luasValue === 0) {
            return '-';
        }
        
        // Hitung target yield (rencana yield)
        const targetYield = target / luasValue;
        
        if (targetYield === 0) {
            return '-';
        }
        
        // Hitung variasi: (Yield - Target Yield) / Target Yield
        const varYield = (yieldNum - targetYield) / targetYield;
        
        // Konversi ke persentase dan gunakan Math.round() untuk membulatkan
        const varYieldPersentase = varYield * 100;
        const roundedVarYield = Math.round(varYieldPersentase);
        
        // Format dan tambahkan %
        return `${roundedVarYield}%`;
    } catch (error) {
        console.error('Error calculating Var Yield Renc. VS Real:', error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung Total Grade Tanpa Crown
 * Total Grade Tanpa Crown (KG) = PH21 (KG) + PH22 (KG) + PNFPG4
 * @param {number|string} ph21 - PH21 dalam KG
 * @param {number|string} ph22 - PH22 dalam KG
 * @param {number|string} pnfpg4 - PNFPG4
 * @returns {string} - Total Grade Tanpa Crown yang diformat
 */
export const calculateTotalGradeTanpaCrown = (ph21, ph22, pnfpg4) => {
    try {
        // Konversi ke number
        const ph21Value = parseFloat(String(ph21).replace(',', '.'));
        const ph22Value = parseFloat(String(ph22).replace(',', '.'));
        const pnfpg4Value = parseFloat(String(pnfpg4).replace(',', '.'));
        
        // Validasi input, set ke 0 jika tidak valid
        const validPh21 = isNaN(ph21Value) ? 0 : ph21Value;
        const validPh22 = isNaN(ph22Value) ? 0 : ph22Value;
        const validPnfpg4 = isNaN(pnfpg4Value) ? 0 : pnfpg4Value;
        
        // Hitung total
        const total = validPh21 + validPh22 + validPnfpg4;
        
        // Format dengan 2 angka desimal
        return total.toFixed(2).replace('.', ',');
    } catch (error) {
        console.error('Error calculating Total Grade Tanpa Crown:', error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung Selisih
 * Selisih = Total Grade Tanpa Crown (KG) - Daily
 * Jika hasilnya 0 maka tampilkan tanda -
 * @param {number|string} totalGradeTanpaCrown - Total Grade Tanpa Crown
 * @param {number|string} daily - Daily
 * @returns {string} - Selisih yang diformat atau "-" jika 0
 */
export const calculateSelisih = (totalGradeTanpaCrown, daily) => {
    try {
        // Konversi ke number
        const totalValue = parseFloat(String(totalGradeTanpaCrown).replace(',', '.'));
        const dailyValue = parseFloat(String(daily).replace(',', '.'));
        
        // Validasi input
        if (isNaN(totalValue) || isNaN(dailyValue)) {
            return '-';
        }
        
        // Hitung selisih
        const selisih = totalValue - dailyValue;
        
        // Jika hasilnya 0, tampilkan tanda -
        if (selisih === 0) {
            return '-';
        }
        
        // Format dengan 2 angka desimal
        return selisih.toFixed(2).replace('.', ',');
    } catch (error) {
        console.error('Error calculating Selisih:', error);
        return '-';
    }
};

/**
 * Fungsi untuk menghitung semua formula sekaligus
 * @param {Object} data - Data yang diperlukan untuk perhitungan
 * @param {string|number} data.tanggal_closing - Tanggal closing
 * @param {string|number} data.target_panen - Target panen
 * @param {string|number} data.real_panen - Real panen
 * @param {string|number} data.luas - Luas lahan
 * @param {string|number} data.ph21 - PH21
 * @param {string|number} data.ph22 - PH22
 * @param {string|number} data.pnfpg4 - PNFPG4
 * @param {string|number} data.daily - Daily
 * @returns {Object} - Hasil semua perhitungan
 */
export const calculateAllFormulas = (data) => {
    const {
        tanggal_closing,
        target_panen,
        real_panen,
        luas,
        ph21,
        ph22,
        pnfpg4,
        daily
    } = data;
    
    // Hitung pencapaian panen terlebih dahulu
    const pencapaianPanen = calculatePencapaianPanen(target_panen, real_panen);
    
    // Hitung yang lainnya
    const luasTerpanen = calculateLuasTerpanen(tanggal_closing, pencapaianPanen, luas);
    const yieldValue = calculateYield(real_panen, luas);
    const rencanaYield = calculateRencanaYield(target_panen, luas);
    const varYieldRencVsReal = calculateVarYieldRencVsReal(yieldValue, target_panen, luas);
    
    // Hitung Total Grade Tanpa Crown
    const totalGradeTanpaCrown = calculateTotalGradeTanpaCrown(ph21, ph22, pnfpg4);
    
    // Hitung Selisih menggunakan total yang baru dihitung
    const selisih = calculateSelisih(totalGradeTanpaCrown, daily);
    
    return {
        pencapaian_panen: pencapaianPanen,
        luas_terpanen: luasTerpanen,
        yield: yieldValue,
        rencana_yield: rencanaYield,
        var_yield_renc_vs_real: varYieldRencVsReal,
        grade_tanpa_crown: totalGradeTanpaCrown, // Update dengan nilai yang dihitung
        selisih: selisih
    };
};

export default {
    calculatePencapaianPanen,
    calculateLuasTerpanen,
    calculateYield,
    calculateRencanaYield,
    calculateVarYieldRencVsReal,
    calculateTotalGradeTanpaCrown,
    calculateSelisih,
    calculateAllFormulas
};